/**
 * API client (docs/API_OUTLINE.md conventions, ARCHITECTURE §5.2).
 *
 *  - base `VITE_API_BASE` (default `/api/v1`), JSON bodies, `credentials: 'include'`
 *  - unwraps `{ data, meta }` envelopes (`apiRequest` → `{ data, meta }`, `apiFetch` → `data`)
 *  - failures become a typed `ApiError` (status, code, message, field `issues`, requestId)
 *  - CSRF double-submit: the `sbg_csrf` cookie is echoed in `x-csrf-token` on POST/PUT/PATCH/DELETE
 *  - silent session refresh: a 401 (except on `/auth/*`) triggers ONE single-flight `POST /auth/refresh`
 *    shared by every concurrent request, then the original request is retried ONCE
 *  - optional `Idempotency-Key`, `AbortSignal`
 *
 * SSR-safe: nothing here touches `window`/`document` at import time.
 */
import {
  COOKIE_NAMES,
  CSRF_HEADER,
  ERROR_CODES,
  IDEMPOTENCY_HEADER,
  REQUEST_ID_HEADER,
} from '@sbg/shared/constants';
import type { ErrorCode } from '@sbg/shared/constants';
import type { ApiErrorBody, ApiMeta, ApiSuccess, FieldIssue } from '@sbg/shared/schemas';
import { getApiBase } from '@/lib/config';

// ── Types ─────────────────────────────────────────────────────────────────────────────────────

/** The server's `ErrorCode`s, plus `NETWORK_ERROR` for requests that never got an answer. */
export type ApiErrorCode = ErrorCode | 'NETWORK_ERROR';

export interface ApiErrorInit {
  status: number;
  code: ApiErrorCode;
  message: string;
  issues?: FieldIssue[];
  details?: unknown;
  requestId?: string | undefined;
}

/** Every failed call surfaces as this — never a raw `Response` or an untyped error. */
export class ApiError extends Error {
  /** HTTP status; `0` when the request never reached the server. */
  readonly status: number;
  readonly code: ApiErrorCode;
  /** Field-level problems (`details` of a `VALIDATION_FAILED` response), empty otherwise. */
  readonly issues: FieldIssue[];
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.issues = init.issues ?? [];
    this.details = init.details;
    this.requestId = init.requestId;
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

export const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { name?: unknown }).name === 'AbortError';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | readonly QueryValue[]>;

export interface ApiRequestOptions {
  method?: HttpMethod;
  /** Plain objects/arrays are sent as JSON; FormData, Blob, URLSearchParams and strings pass through. */
  body?: unknown;
  query?: QueryParams;
  headers?: Record<string, string>;
  signal?: AbortSignal | null | undefined;
  /** Sent as `Idempotency-Key` (use `newIdempotencyKey()`), e.g. for POST /enquiries. */
  idempotencyKey?: string;
  /**
   * Try a silent `POST /auth/refresh` + one retry when the response is 401.
   * Default: `true`, except for `/auth/*` endpoints (which would otherwise loop).
   */
  refreshOn401?: boolean;
  credentials?: RequestCredentials;
}

export interface ApiResult<T> {
  data: T;
  meta?: ApiMeta | undefined;
}

// ── Helpers ───────────────────────────────────────────────────────────────────────────────────

const UNSAFE_METHODS: ReadonlySet<string> = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const KNOWN_CODES: ReadonlySet<string> = new Set(Object.values(ERROR_CODES));

const STATUS_CODES: Readonly<Record<number, ErrorCode>> = {
  400: ERROR_CODES.VALIDATION_FAILED,
  401: ERROR_CODES.UNAUTHENTICATED,
  403: ERROR_CODES.FORBIDDEN,
  404: ERROR_CODES.NOT_FOUND,
  409: ERROR_CODES.CONFLICT,
  413: ERROR_CODES.PAYLOAD_TOO_LARGE,
  415: ERROR_CODES.UNSUPPORTED_MEDIA,
  422: ERROR_CODES.VALIDATION_FAILED,
  423: ERROR_CODES.ACCOUNT_LOCKED,
  429: ERROR_CODES.RATE_LIMITED,
};

/** `true` for `/auth/*` endpoints: a 401 there is an answer, not a reason to refresh. */
export const isAuthPath = (path: string): boolean => path === '/auth' || path.startsWith('/auth/');

/** A fresh random key for `Idempotency-Key` (one per user intent, reused on retries of that intent). */
export function newIdempotencyKey(): string {
  const { crypto } = globalThis;
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // `randomUUID` only exists in secure contexts; build a v4 UUID by hand otherwise.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isEnvelope(body: unknown): body is ApiSuccess<unknown> {
  return isRecord(body) && 'data' in body;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined' || document.cookie === '') return null;
  for (const part of document.cookie.split('; ')) {
    const separator = part.indexOf('=');
    if (separator === -1 || part.slice(0, separator) !== name) continue;
    const raw = part.slice(separator + 1);
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return null;
}

// ── CSRF (double-submit cookie) ───────────────────────────────────────────────────────────────

let memoryCsrfToken: string | null = null;
let csrfInflight: Promise<void> | null = null;

/** Keep a token handed out in a response body (covers cross-origin previews where the cookie is unreadable). */
export function setCsrfToken(token: string | null): void {
  memoryCsrfToken = token;
}

/** The CSRF token to echo: the readable cookie (`__Host-` prefixed in production) or the last body token. */
export function getCsrfToken(): string | null {
  return (
    readCookie(`__Host-${COOKIE_NAMES.csrf}`) ?? readCookie(COOKIE_NAMES.csrf) ?? memoryCsrfToken
  );
}

function pickCsrfToken(body: unknown): string | null {
  const data = isEnvelope(body) ? body.data : body;
  return isRecord(data) && typeof data.csrfToken === 'string' ? data.csrfToken : null;
}

/** Make sure a CSRF token exists before the first unsafe request (single-flight `GET /auth/csrf`). */
function ensureCsrfToken(): Promise<void> {
  if (typeof window === 'undefined' || getCsrfToken() !== null) return Promise.resolve();
  if (csrfInflight) return csrfInflight;
  const flight = (async () => {
    try {
      const { response, body } = await send('/auth/csrf', { method: 'GET' });
      if (response.ok) {
        const token = pickCsrfToken(body);
        if (token) setCsrfToken(token);
      }
    } catch {
      /* proceed without a token — the server decides */
    }
  })();
  csrfInflight = flight;
  void flight.finally(() => {
    if (csrfInflight === flight) csrfInflight = null;
  });
  return flight;
}

// ── Transport ─────────────────────────────────────────────────────────────────────────────────

interface Sent {
  response: Response;
  body: unknown;
}

const isQueryList = (value: QueryValue | readonly QueryValue[]): value is readonly QueryValue[] =>
  Array.isArray(value);

function buildUrl(path: string, query: QueryParams | undefined): string {
  const url = `${getApiBase()}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    for (const item of isQueryList(value) ? value : [value]) {
      if (item === undefined || item === null || item === '') continue;
      params.append(key, String(item));
    }
  }
  const qs = params.toString();
  if (qs === '') return url;
  return `${url}${url.includes('?') ? '&' : '?'}${qs}`;
}

function serializeBody(body: unknown, headers: Headers): BodyInit | undefined {
  if (body === undefined || body === null) return undefined;
  if (
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer
  ) {
    return body;
  }
  if (!headers.has('content-type')) headers.set('content-type', 'application/json');
  return JSON.stringify(body);
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined;
  const text = await response.text();
  if (text === '') return undefined;
  const type = response.headers.get('content-type') ?? '';
  if (type.includes('json')) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

async function send(path: string, options: ApiRequestOptions): Promise<Sent> {
  const method = options.method ?? 'GET';
  const headers = new Headers({ accept: 'application/json', ...options.headers });

  if (UNSAFE_METHODS.has(method)) {
    await ensureCsrfToken();
    const token = getCsrfToken();
    if (token && !headers.has(CSRF_HEADER)) headers.set(CSRF_HEADER, token);
  }
  if (options.idempotencyKey) headers.set(IDEMPOTENCY_HEADER, options.idempotencyKey);

  const init: RequestInit = {
    method,
    headers,
    credentials: options.credentials ?? 'include',
    signal: options.signal ?? null,
  };
  const body = serializeBody(options.body, headers);
  if (body !== undefined) init.body = body;

  try {
    const response = await fetch(buildUrl(path, options.query), init);
    return { response, body: await readBody(response) };
  } catch (error) {
    if (isAbortError(error)) throw error; // let callers/TanStack Query treat it as a cancellation
    throw new ApiError({
      status: 0,
      code: 'NETWORK_ERROR',
      message: 'The network request failed.',
      details: error instanceof Error ? error.message : undefined,
    });
  }
}

// ── Error normalisation ───────────────────────────────────────────────────────────────────────

function toIssues(details: unknown): FieldIssue[] {
  if (!Array.isArray(details)) return [];
  const issues: FieldIssue[] = [];
  for (const item of details as unknown[]) {
    if (!isRecord(item) || typeof item.message !== 'string') continue;
    issues.push({ path: typeof item.path === 'string' ? item.path : '', message: item.message });
  }
  return issues;
}

function codeFromStatus(status: number): ErrorCode {
  return STATUS_CODES[status] ?? ERROR_CODES.INTERNAL;
}

function isErrorBody(body: unknown): body is ApiErrorBody {
  return isRecord(body) && isRecord(body.error) && typeof body.error.message === 'string';
}

function toApiError(response: Response, body: unknown): ApiError {
  const headerRequestId = response.headers.get(REQUEST_ID_HEADER) ?? undefined;
  if (isErrorBody(body)) {
    const { error } = body;
    return new ApiError({
      status: response.status,
      // the wire value may be newer than this client: only trust codes we know
      code: KNOWN_CODES.has(error.code) ? error.code : codeFromStatus(response.status),
      message: error.message,
      details: error.details,
      issues: toIssues(error.details),
      requestId: typeof error.requestId === 'string' ? error.requestId : headerRequestId,
    });
  }
  return new ApiError({
    status: response.status,
    code: codeFromStatus(response.status),
    message: response.statusText || `Request failed with status ${String(response.status)}.`,
    requestId: headerRequestId,
  });
}

function settle<T>({ response, body }: Sent): ApiResult<T> {
  if (!response.ok) throw toApiError(response, body);
  if (isEnvelope(body)) return { data: body.data as T, meta: body.meta };
  return { data: body as T };
}

// ── Silent refresh (single-flight) ────────────────────────────────────────────────────────────

let refreshInflight: Promise<boolean> | null = null;
let sessionExpiredHandler: (() => void) | null = null;

/** Called when the refresh token is definitively rejected (the user is signed out). */
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  sessionExpiredHandler = handler;
}

async function performRefresh(): Promise<boolean> {
  try {
    const { response, body } = await send('/auth/refresh', { method: 'POST' });
    if (response.ok) {
      const token = pickCsrfToken(body);
      if (token) setCsrfToken(token);
      return true;
    }
    if (response.status === 401 || response.status === 403) sessionExpiredHandler?.();
    return false;
  } catch {
    return false;
  }
}

/**
 * Rotate the session cookies. Concurrent callers share ONE in-flight `POST /auth/refresh`;
 * resolves `true` when the session was renewed.
 */
export function refreshSession(): Promise<boolean> {
  if (refreshInflight) return refreshInflight;
  const flight = performRefresh();
  refreshInflight = flight;
  void flight.finally(() => {
    if (refreshInflight === flight) refreshInflight = null;
  });
  return flight;
}

/** Test/HMR helper: forget in-flight work and cached tokens. */
export function resetApiClientState(): void {
  refreshInflight = null;
  csrfInflight = null;
  memoryCsrfToken = null;
  sessionExpiredHandler = null;
}

// ── Public API ────────────────────────────────────────────────────────────────────────────────

/** Perform a request and return the unwrapped envelope: `{ data, meta }`. Throws `ApiError`. */
export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiResult<T>> {
  const first = await send(path, options);
  const mayRefresh = options.refreshOn401 ?? !isAuthPath(path);
  if (first.response.status === 401 && mayRefresh && !options.signal?.aborted) {
    if (await refreshSession()) return settle<T>(await send(path, options)); // retried exactly once
  }
  return settle<T>(first);
}

/** Like `apiRequest`, but resolves just `data`. */
export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  return (await apiRequest<T>(path, options)).data;
}
